import { notifyError, notifyInfo, notifySuccess } from '../shared/notify';

const ENV = process.env.REACT_APP_ENV ?? 'development';
const DEFAULT_AUTHORITY = ENV === 'production'
  ? 'https://api.boma.eu/identity'
  : 'https://apitest.boma.eu/identity';

const AUTHORITY = process.env.REACT_APP_AUTHORITY ?? DEFAULT_AUTHORITY;
const CLIENT_ID = 'boma-chatbot';
const REDIRECT_URI = `${window.location.origin}/auth-callback.html`;
const SCOPES = 'openid profile offline_access chatbot';

function b64urlFromBytes(bytes: Uint8Array) {
  let str = btoa(String.fromCharCode(...Array.from(bytes)));
  return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
function randomString(len = 43) {
  const abc = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let s = '';
  const arr = new Uint8Array(len);
  (crypto?.getRandomValues ? crypto.getRandomValues(arr) : arr.fill(0)).forEach(v => (s += abc[v % abc.length]));
  return s;
}
async function sha256(text: string) {
  const data = new TextEncoder().encode(text);
  return await crypto.subtle.digest('SHA-256', data);
}

function decodeJwt(token?: string | null) {
  if (!token) return undefined;
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return undefined; }
}

export function isAuthenticated(): boolean {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  const exp = Number(localStorage.getItem('token_expiry') || 0);
  if (exp && Date.now() < exp) return true;
  // Fallback: decode JWT 'exp' if present
  try {
    const claims = decodeJwt(token as string) as any;
    if (claims?.exp) {
      const ms = claims.exp * 1000;
      localStorage.setItem('token_expiry', String(ms));
      return Date.now() < ms;
    }
  } catch {}
  return false;
}
export function getAccessToken(): string | undefined {
  return localStorage.getItem('access_token') || undefined;
}
export function getUserId(): string | undefined {
  const stored = localStorage.getItem('user_id') || undefined;
  if (stored) return stored;
  const claims = decodeJwt(getAccessToken());
  const uid = claims?.sub || claims?.GEBRUIKER || claims?.upn || claims?.preferred_username || claims?.email;
  if (uid) localStorage.setItem('user_id', String(uid));
  return uid || undefined;
}

export async function login(): Promise<void> {
  notifyInfo('Inloggen gestart', 'We openen het inlogvenster…');
  const state = randomString(16);
  const verifier = randomString(64);
  const challenge = b64urlFromBytes(new Uint8Array(await sha256(verifier)));
  const tx = JSON.stringify({ verifier, createdAt: Date.now() });
  localStorage.setItem(`oauth_tx_${state}`, tx);
  sessionStorage.setItem(`oauth_tx_${state}`, tx);

  const authorizeUrl = `${AUTHORITY}/connect/authorize?response_type=code&client_id=${encodeURIComponent(CLIENT_ID)}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=${encodeURIComponent(SCOPES)}&code_challenge=${challenge}&code_challenge_method=S256&state=${encodeURIComponent(state)}`;
  // Gebruik lokale bootstrap als eerste hop voor Office dialog
  const bootstrapUrl = `${window.location.origin}/auth-dialog-bootstrap.html#${new URLSearchParams({ s: state, v: verifier, u: encodeURIComponent(authorizeUrl) }).toString()}`;

  const Office = (window as any).Office;
  if (Office?.context?.ui?.displayDialogAsync) {
    await new Promise<void>((resolve, reject) => {
      let activity = false;
      const inactivityTimer = setTimeout(() => {
        if (!activity) {
          notifyInfo('Nog geen antwoord van loginpagina…', 'Controleer AppDomains in het manifest en netwerktoegang.');
        }
      }, 8000);

      Office.context.ui.displayDialogAsync(bootstrapUrl, { height: 60, width: 50, displayInIframe: false }, (res: any) => {
        if (res.status !== Office.AsyncResultStatus.Succeeded) {
          const msg = res?.error?.message || `code ${res?.error?.code ?? 'onbekend'}`;
          notifyError('Dialoog openen mislukt', msg);
          clearTimeout(inactivityTimer);
          return reject(res.error);
        }
        const dialog = res.value;
        notifyInfo('Loginvenster geopend');
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (evt: any) => {
          activity = true;
          try {
            const data = JSON.parse(evt.message || '{}');
            if (data?.success) {
              if (data.access_token) localStorage.setItem('access_token', data.access_token);
              if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
              if (data.expires_at) localStorage.setItem('token_expiry', String(data.expires_at));
              else if (data.expires_in) localStorage.setItem('token_expiry', String(Date.now() + Number(data.expires_in) * 1000));
              // user_id uit token
              try {
                const claims = decodeJwt(data.access_token);
                const uid = claims?.sub || claims?.GEBRUIKER || claims?.upn || claims?.preferred_username || claims?.email;
                if (uid) localStorage.setItem('user_id', String(uid));
              } catch {}
              dialog.close();
              notifySuccess('Ingelogd');
              clearTimeout(inactivityTimer);
              resolve();
            } else {
              dialog.close();
              const err = new Error(data?.error || 'auth_failed');
              notifyError('Inloggen mislukt', err.message);
              clearTimeout(inactivityTimer);
              reject(err);
            }
          } catch (e) {
            dialog.close();
            notifyError('Inloggen mislukt', (e as Error)?.message || e);
            clearTimeout(inactivityTimer);
            reject(e);
          }
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          notifyError('Inloggen geannuleerd', 'Het venster werd gesloten.');
          clearTimeout(inactivityTimer);
          reject(new Error('dialog_closed'));
        });
      });
    });
  } else {
    notifyInfo('Doorverwijzing naar loginpagina…');
    window.location.href = authorizeUrl;
  }
}

export function logout(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token_expiry');
  localStorage.removeItem('user_id');

  const Office = (window as any).Office;
  if (Office) {
    // In een taskpane volstaat refresh
    window.location.reload();
  } else {
    const postLogout = encodeURIComponent(`${window.location.origin}/`);
    const logoutUrl = `${AUTHORITY}/connect/endsession?post_logout_redirect_uri=${postLogout}&client_id=${encodeURIComponent(CLIENT_ID)}`;
    window.location.href = logoutUrl;
  }
}

export function authGetUserLanguage(): string | null {
  const token = localStorage.getItem('access_token');
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    
    // Check verschillende mogelijke velden in token
    const lang = payload.language || payload.lang || payload.locale || payload.preferred_language;
    
    if (lang) {
      console.log('[auth] User language from token:', lang);
      return lang;
    }
    
    return null;
  } catch (err) {
    console.error('[auth] Error parsing token for language:', err);
    return null;
  }
}